import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api, { fmtError } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Users, Coins as CoinsIcon, Flag, ArrowLeft, Loader2,
  Banknote, CheckCircle2, XCircle, Ban, RotateCcw, TrendingUp, Activity,
  AlertTriangle, ExternalLink, LifeBuoy,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, sub, tint = "#14C8B0" }) {
  return (
    <div className="card-nb bg-white p-5">
      <div className="flex items-start justify-between">
        <div
          className="w-11 h-11 rounded-xl border-2 border-ink flex items-center justify-center"
          style={{ background: tint + "22" }}
        >
          <Icon className="w-5 h-5 text-ink" strokeWidth={2.5} />
        </div>
      </div>
      <div className="mt-4 font-display font-black text-3xl tracking-tight">{value}</div>
      <div className="font-display font-bold text-xs uppercase tracking-widest text-[#7C828D] mt-1">{label}</div>
      {sub && <div className="text-xs text-[#3D424B] mt-2">{sub}</div>}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, action }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-ink" strokeWidth={2.5} />
        <h2 className="font-display font-black text-xl">{title}</h2>
      </div>
      {action}
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [reports, setReports] = useState([]);
  const [cashouts, setCashouts] = useState([]);
  const [reportStatus, setReportStatus] = useState("open");
  const [cashoutStatus, setCashoutStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const hasPermission = (p) => {
    if (!user) return false;
    if (user.is_master || user.email === "mustafahaidari03@gmail.com") return true;
    return (user.admin_permissions || []).includes(p);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const promises = [];
      
      if (hasPermission("metrics")) {
        promises.push(api.get("/admin/metrics").then(res => ({ type: "metrics", data: res.data })));
      } else {
        promises.push(Promise.resolve({ type: "metrics", data: null }));
      }
      
      if (hasPermission("reports")) {
        promises.push(api.get(`/admin/reports?status=${reportStatus}`).then(res => ({ type: "reports", data: res.data })));
      } else {
        promises.push(Promise.resolve({ type: "reports", data: [] }));
      }
      
      if (hasPermission("cashouts")) {
        promises.push(api.get(`/admin/cashouts?status=${cashoutStatus}`).then(res => ({ type: "cashouts", data: res.data })));
      } else {
        promises.push(Promise.resolve({ type: "cashouts", data: [] }));
      }
      
      const results = await Promise.all(promises);
      results.forEach(res => {
        if (res.type === "metrics") setMetrics(res.data);
        if (res.type === "reports") setReports(res.data || []);
        if (res.type === "cashouts") setCashouts(res.data || []);
      });
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      else toast.error(fmtError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [reportStatus, cashoutStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveReport = async (id) => {
    try { await api.post(`/admin/reports/${id}/resolve`, {}); toast.success(t("adminDash.reportResolved")); loadAll(); }
    catch (e) { toast.error(fmtError(e)); }
  };
  const dismissReport = async (id) => {
    try { await api.post(`/admin/reports/${id}/dismiss`, {}); toast.success(t("adminDash.reportDismissed")); loadAll(); }
    catch (e) { toast.error(fmtError(e)); }
  };
  const banUser = async (uid) => {
    if (!window.confirm(t("adminDash.confirmBan"))) return;
    try { await api.post(`/admin/users/${uid}/ban`, { reason: "Reported by community" }); toast.success(t("adminDash.userBanned")); loadAll(); }
    catch (e) { toast.error(fmtError(e)); }
  };
  const unbanUser = async (uid) => {
    try { await api.post(`/admin/users/${uid}/unban`); toast.success(t("adminDash.userUnbanned")); loadAll(); }
    catch (e) { toast.error(fmtError(e)); }
  };
  const markPaid = async (id) => {
    const ref = window.prompt(t("adminDash.paymentReferencePrompt")) || "";
    try { await api.post(`/admin/cashouts/${id}/mark-paid`, { reference: ref }); toast.success(t("adminDash.cashoutMarkedPaid")); loadAll(); }
    catch (e) { toast.error(fmtError(e)); }
  };
  const cancelCashout = async (id) => {
    if (!window.confirm(t("adminDash.confirmCancelCashout"))) return;
    const reason = window.prompt(t("adminDash.cancelReasonPrompt")) || "";
    try { const r = await api.post(`/admin/cashouts/${id}/cancel`, { reference: reason }); toast.success(`${t("adminDash.cashoutCancelled")} (+€{r.data.coins_refunded} coins)`); loadAll(); }
    catch (e) { toast.error(fmtError(e)); }
  };

  if (forbidden) {
    return (
      <div className="max-w-md mx-auto pt-24 px-6 text-center">
        <div className="card-nb bg-white p-8">
          <ShieldCheck className="w-10 h-10 text-[#FF7A6B] mx-auto" strokeWidth={2.5} />
          <h2 className="mt-4 font-display font-black text-2xl">{t("adminDash.forbidden")}</h2>
          <p className="mt-2 text-[#3D424B]">{t("adminDash.adminOnly")}</p>
          <button onClick={() => nav("/feed")} className="btn-nb btn-nb-primary mt-6" data-testid="back-to-feed-btn">
            {t("common.back")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 lg:px-8 lg:py-10 pb-28">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => nav(-1)} className="btn-nb p-2.5 sm:p-3" data-testid="admin-back-btn">
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <div>
            <div className="font-script text-lg sm:text-2xl text-[#14C8B0]">{t("adminDash.kicker")}</div>
            <h1 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight">{t("adminDash.title")}</h1>
          </div>
        </div>
        <div className="flex flex-col gap-2 w-full sm:w-80">
          <div className="grid grid-cols-2 gap-2 w-full">
            {hasPermission("tickets") && (
              <Link to="/admin/tickets" className="btn-nb !px-3 !py-2 text-xs sm:text-sm font-bold shrink-0 justify-center text-center whitespace-nowrap" data-testid="admin-tickets-link">
                <LifeBuoy className="w-4 h-4 me-1.5 inline" />
                {t("adminTickets.title")}
              </Link>
            )}
            {hasPermission("verifications") && (
              <Link to="/admin/verifications" className="btn-nb btn-nb-dark !px-3 !py-2 text-xs sm:text-sm font-bold shrink-0 justify-center text-center whitespace-nowrap" data-testid="admin-id-link">
                <ShieldCheck className="w-4 h-4 me-1.5 inline" />
                {t("adminDash.idVerifications")}
              </Link>
            )}
          </div>
          {hasPermission("users") && (
            <Link to="/admin/accounts" className="btn-nb btn-nb-dark !px-3 !py-2 text-xs sm:text-sm font-bold shrink-0 justify-center text-center whitespace-nowrap w-full" data-testid="admin-accounts-link">
              <Users className="w-4 h-4 me-1.5 inline" />
              Members List
            </Link>
          )}
        </div>
      </div>

      {loading && !metrics ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>
      ) : (
        <>
          {/* ============ METRICS ============ */}
          {metrics && (
            <>
              <SectionHeader icon={Activity} title={t("adminDash.users")} />
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-10">
                <StatCard icon={Users} label={t("adminDash.totalUsers")} value={metrics.users.total} sub={`+${metrics.users.new_24h} ${t("adminDash.last24h")}`} tint="#14C8B0" />
                <StatCard icon={ShieldCheck} label={t("adminDash.verified")} value={metrics.users.verified} tint="#F9C349" />
                <StatCard icon={TrendingUp} label={t("adminDash.vipActive")} value={metrics.users.vip_active} tint="#FF7A6B" />
                <StatCard icon={Activity} label={t("adminDash.active30d")} value={metrics.users.active_30d} sub={`+${metrics.users.new_7d} ${t("adminDash.new7d")}`} tint="#BFE9D6" />
              </div>

              <SectionHeader icon={CoinsIcon} title={t("adminDash.economy")} />
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-10">
                <StatCard icon={CoinsIcon} label={t("adminDash.coinsOutstanding")} value={metrics.economy.coins_outstanding.toLocaleString()} sub={`≈ €${metrics.economy.coins_outstanding_eur_equivalent}`} tint="#F9C349" />
                <StatCard icon={TrendingUp} label={t("adminDash.stripeRevenue")} value={`€${metrics.economy.stripe_revenue_total_eur.toLocaleString()}`} sub={`${metrics.economy.stripe_purchase_count} ${t("adminDash.purchases")}`} tint="#14C8B0" />
                <StatCard icon={Banknote} label={t("adminDash.cashoutsPending")} value={metrics.economy.cashouts_pending_count} sub={`€${metrics.economy.cashouts_pending_total_eur.toLocaleString()} ${t("adminDash.pending")}`} tint="#FF7A6B" />
                <StatCard icon={CheckCircle2} label={t("adminDash.cashoutsPaid")} value={metrics.economy.cashouts_paid_count} sub={`€${metrics.economy.cashouts_paid_total_eur.toLocaleString()}`} tint="#BFE9D6" />
              </div>

              <SectionHeader icon={Activity} title={t("adminDash.content")} />
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-12">
                <StatCard icon={Activity} label={t("adminDash.activitiesTotal")} value={metrics.content.activities_total} sub={`+${metrics.content.activities_7d} ${t("adminDash.new7d")}`} tint="#D9CFFF" />
                <StatCard icon={Activity} label={t("adminDash.postsTotal")} value={metrics.content.posts_total} tint="#FFE08A" />
                <StatCard icon={Flag} label={t("adminDash.openReports")} value={metrics.moderation.reports_open} tint="#FF7A6B" />
              </div>
            </>
          )}

          {/* ============ REPORTS QUEUE ============ */}
          {hasPermission("reports") && (
            <div className="mb-12">
              <SectionHeader
                icon={Flag}
                title={t("adminDash.reportsQueue")}
                action={
                  <div className="flex flex-col gap-1.5 items-end">
                    <div className="flex gap-1.5">
                      {["open", "resolved"].map((s) => (
                        <button key={s}
                          onClick={() => setReportStatus(s)}
                          className={`px-3 py-1.5 rounded-xl border-2 border-ink text-xs font-display font-bold ${reportStatus === s ? "bg-ink text-white" : "bg-white"}`}
                          data-testid={`reports-filter-€{s}`}>
                          {t(`adminDash.statusLabels.${s}`)}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-1.5">
                      {["dismissed", "all"].map((s) => (
                        <button key={s}
                          onClick={() => setReportStatus(s)}
                          className={`px-3 py-1.5 rounded-xl border-2 border-ink text-xs font-display font-bold ${reportStatus === s ? "bg-ink text-white" : "bg-white"}`}
                          data-testid={`reports-filter-€{s}`}>
                          {t(`adminDash.statusLabels.${s}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                }
              />
              {reports.length === 0 ? (
                <div className="card-nb bg-white p-8 text-center text-[#7C828D]">{t("adminDash.noReports")}</div>
              ) : (
                <div className="space-y-3">
                  {reports.map((r) => (
                    <div key={r.id} className="card-nb bg-white p-5" data-testid={`report-€{r.id}`}>
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="px-2 py-1 rounded-full bg-[#FFE08A] border-2 border-ink text-xs font-display font-bold">
                              {r.target_type}
                            </span>
                            <span className={`px-2 py-1 rounded-full border-2 border-ink text-xs font-display font-bold ${
                              r.status === "open" ? "bg-[#FF7A6B] text-white" :
                              r.status === "resolved" ? "bg-[#BFE9D6]" : "bg-[#EFEEE6]"
                            }`}>{r.status}</span>
                            <span className="text-xs font-mono text-[#7C828D]">{r.created_at?.slice(0, 16).replace("T", " ")}</span>
                          </div>
                          <div className="mt-3 font-medium text-sm">
                            <span className="text-[#7C828D]">{t("adminDash.reportReason")}: </span>{r.reason || "—"}
                          </div>
                          {r.reporter && (
                            <div className="mt-2 text-xs text-[#3D424B]">
                              {t("adminDash.reportedBy")}: <span className="font-bold">{r.reporter.name}</span> ({r.reporter.email})
                            </div>
                          )}
                          {r.target_user && (
                            <div className="mt-1 text-xs text-[#3D424B]">
                              {t("adminDash.targetUser")}: <span className="font-bold">{r.target_user.name}</span> ({r.target_user.email})
                              {r.target_user.banned && <span className="ms-2 px-2 py-0.5 rounded-full bg-[#FF7A6B] text-white text-[10px] font-bold">BANNED</span>}
                            </div>
                          )}
                          {r.target_type !== "user" && r.target_id && (
                            <div className="mt-1 text-xs text-[#3D424B] font-mono">id: {r.target_id}</div>
                          )}
                        </div>
                        {r.status === "open" && (
                          <div className="flex flex-wrap gap-2">
                            <button onClick={() => resolveReport(r.id)} className="btn-nb btn-nb-primary text-xs" data-testid={`resolve-€{r.id}`}>
                              <CheckCircle2 className="w-4 h-4 me-1 inline" />{t("adminDash.resolve")}
                            </button>
                            <button onClick={() => dismissReport(r.id)} className="btn-nb text-xs" data-testid={`dismiss-€{r.id}`}>
                              <XCircle className="w-4 h-4 me-1 inline" />{t("adminDash.dismiss")}
                            </button>
                            {r.target_user && !r.target_user.banned && (
                              <button onClick={() => banUser(r.target_user.id)} className="btn-nb btn-nb-coral text-xs" data-testid={`ban-€{r.target_user.id}`}>
                                <Ban className="w-4 h-4 me-1 inline" />{t("adminDash.banUser")}
                              </button>
                            )}
                            {r.target_user?.banned && (
                              <button onClick={() => unbanUser(r.target_user.id)} className="btn-nb text-xs" data-testid={`unban-€{r.target_user.id}`}>
                                <RotateCcw className="w-4 h-4 me-1 inline" />{t("adminDash.unban")}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {r.admin_note && (
                        <div className="mt-3 px-3 py-2 rounded-xl bg-[#EFEEE6] text-xs text-[#3D424B]">
                          <span className="font-bold">{t("adminDash.adminNote")}:</span> {r.admin_note}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ============ CASHOUTS QUEUE ============ */}
          {hasPermission("cashouts") && (
            <div>
              <SectionHeader
                icon={Banknote}
                title={t("adminDash.cashoutsQueue")}
                action={
                  <div className="flex flex-col gap-1.5 items-end">
                    <div className="flex gap-1.5">
                      {["pending", "transferred"].map((s) => (
                        <button key={s}
                          onClick={() => setCashoutStatus(s)}
                          className={`px-3 py-1.5 rounded-xl border-2 border-ink text-xs font-display font-bold ${cashoutStatus === s ? "bg-ink text-white" : "bg-white"}`}
                          data-testid={`cashouts-filter-€{s}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-1.5">
                      {["paid", "cancelled", "all"].map((s) => (
                        <button key={s}
                          onClick={() => setCashoutStatus(s)}
                          className={`px-3 py-1.5 rounded-xl border-2 border-ink text-xs font-display font-bold ${cashoutStatus === s ? "bg-ink text-white" : "bg-white"}`}
                          data-testid={`cashouts-filter-€{s}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                }
              />
              {cashouts.length === 0 ? (
                <div className="card-nb bg-white p-8 text-center text-[#7C828D]">{t("adminDash.noCashouts")}</div>
              ) : (
                <div className="space-y-3">
                  {cashouts.map((c) => (
                    <div key={c.id} className="card-nb bg-white p-5" data-testid={`cashout-€{c.id}`}>
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="px-2 py-1 rounded-full bg-[#F9C349] border-2 border-ink text-xs font-display font-bold">
                              {c.coins} T-Coins
                            </span>
                            <span className="px-2 py-1 rounded-full bg-[#BFE9D6] border-2 border-ink text-xs font-display font-bold">
                              ${c.net_eur} net
                            </span>
                            <span className={`px-2 py-1 rounded-full border-2 border-ink text-xs font-display font-bold ${
                              c.status === "pending" ? "bg-[#FF7A6B] text-white" :
                              c.status === "paid" || c.status === "transferred" ? "bg-[#BFE9D6]" :
                              c.status === "cancelled" ? "bg-[#EFEEE6]" : "bg-white"
                            }`}>{c.status}</span>
                            <span className="px-2 py-1 rounded-full bg-white border-2 border-ink text-xs font-mono">
                              {c.payout_method || "manual"}
                            </span>
                            <span className="text-xs font-mono text-[#7C828D]">{c.created_at?.slice(0, 16).replace("T", " ")}</span>
                          </div>
                          {c.user && (
                            <div className="mt-3 text-sm">
                              <span className="font-bold">{c.user.name}</span> · {c.user.email}
                            </div>
                          )}
                          {c.payout_method === "manual" && (c.bank_name || c.iban) && (
                            <div className="mt-2 text-xs text-[#3D424B] font-mono">
                              {c.bank_name || "—"} · {c.iban || "—"} · {c.holder || "—"} {c.country && `(${c.country})`}
                            </div>
                          )}
                          {c.stripe_transfer_id && (
                            <div className="mt-2 text-xs text-[#3D424B] font-mono flex items-center gap-1">
                              <ExternalLink className="w-3 h-3" />Stripe transfer: {c.stripe_transfer_id}
                            </div>
                          )}
                          <div className="mt-2 text-xs text-[#7C828D]">
                            Gross: ${c.gross_eur} · Fee: ${c.fee_eur} · Net: ${c.net_eur}
                          </div>
                        </div>
                        {c.status === "pending" && c.payout_method !== "stripe_connect" && (
                          <div className="flex flex-wrap gap-2">
                            <button onClick={() => markPaid(c.id)} className="btn-nb btn-nb-primary text-xs" data-testid={`mark-paid-€{c.id}`}>
                              <CheckCircle2 className="w-4 h-4 me-1 inline" />{t("adminDash.markPaid")}
                            </button>
                            <button onClick={() => cancelCashout(c.id)} className="btn-nb btn-nb-coral text-xs" data-testid={`cancel-cashout-€{c.id}`}>
                              <XCircle className="w-4 h-4 me-1 inline" />{t("adminDash.cancelAndRefund")}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
